import {
  Component,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { TabBase } from '@core/base/tab.base';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { VehicleType } from '@shared/constants/vehicle-type';
import { VehicleLeasingTabForm } from './interfaces';
import { VehicleLeasingChartHelperService } from './vehicle-leasing-chart-helper.service';
import { VehicleLeasingTabService } from './vehicle-leasing-tab.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { combineLatest, map, Observable, take, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { EChartsCoreOption } from 'echarts/core';

@Component({
  templateUrl: './vehicle-leasing-tab.component.html',
  providers: [VehicleLeasingTabService, VehicleLeasingChartHelperService],
  styleUrls: ['./vehicle-leasing-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehicleLeasingTabComponent
  extends TabBase
  implements PendingChanges
{
  private api = inject(RestApiService);
  readonly tab = inject(VehicleLeasingTabService);
  private agGridEntityService = inject(SplitterWithGridHelperService);
  private readonly chartHelper = inject(VehicleLeasingChartHelperService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private oidc = inject(OidcSecurityService);

  formGroup: FormGroup<FormOf<VehicleLeasingTabForm>>;

  private destroyRef = inject(DestroyRef);

  chartOption: EChartsCoreOption;

  constructor() {
    super(inject(ActivatedRoute).parent!);
    const tab = this.tab;

    this.formGroup = DvFormUtils.createFormGroup([
      ...tab.financingElements,
      ...tab.statusElements,
    ]);
    this.subscribeToSelectedEntityId();
  }

  onSave(): void {
    this.performSave().subscribe();
  }

  performSave(): Observable<boolean> {
    const data = this.formGroup.value;
    delete data.FinancingMileageAtStart;
    delete data.Mileage;
    delete data.FinancingPeriodEnd;
    delete data.FinancingRemainingMonths;
    delete data.FinancingRemainingKm;
    delete data.FinancingRemainingKmMonth;
    delete data.Username;
    delete data.VehicleTypeDescription;

    const entityId = this.entityId$.value;
    return this.api.put(Entity.Vehicle, entityId, data).pipe(
      take(1),
      tap(() => {
        this.formGroup.markAsPristine();
        this.agGridEntityService.refreshRowData(entityId);
      }),
      map(() => {
        this.getLeasingInfo(this.entityId$.value);
        return true;
      })
    );
  }

  pendingChangesSave(): Observable<boolean> {
    return this.performSave();
  }

  hasChanges(): boolean {
    return this.formGroup.dirty && this.formGroup.valid;
  }

  private subscribeToSelectedEntityId(): void {
    this.entityId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      this.getLeasingInfo(id);
    });
  }

  private getLeasingInfo(id: string): void {
    if (!id) return;
    const keys = Object.keys(this.formGroup.controls);
    this.api
      .getInfo<VehicleLeasingTabForm>(Entity.Vehicle, id, keys)
      .subscribe(data => {
        this.formGroup.reset();
        this.formGroup.patchValue(data);
        this.handleAccessRestrictions(
          !!data.ShowRecord,
          data.Username || '',
          data.VehicleTypeDescription
        );

        this.chartOption = this.chartHelper.getChartOptions(
          data.FinancingPeriodMonths,
          data.FinancingAllowedKm,
          data.FinancingPeriodChart
        );
      });
  }

  private handleAccessRestrictions(
    showRecord: boolean,
    defaultDriver: string,
    vehicleType?: VehicleType | undefined
  ): void {
    combineLatest([
      this.permissionsService.hasPermissions([RolesAndPermissions.VehicleEdit]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.VehicleEditMyPrivate,
      ]),
      this.oidc.getUserData(),
    ])
      .pipe(take(1))
      .subscribe(([hasEdit, hasEditMyPrivate, { email }]) => {
        const isUsersPrivateVehicle =
          email.toLowerCase() === defaultDriver.toLowerCase() &&
          vehicleType === VehicleType.Personal;
        const shouldFormBeDisabled =
          (!hasEdit && !hasEditMyPrivate) ||
          (!hasEdit && hasEditMyPrivate && !isUsersPrivateVehicle) ||
          !showRecord;

        this.tab.financingElements.forEach(el => {
          const key = el.options.key;
          if (!key) return;
          shouldFormBeDisabled
            ? this.formGroup.get(key)?.disable()
            : this.formGroup.get(key)?.enable();
        });
      });
  }
}
